import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface ReportProject {
  project_id: number
  name: string
  address: string | null
  city: string | null
  district: string | null
  latitude: number | null
  longitude: number | null
  status: string | null
  listing_date: string | null
  sold_out_date: string | null
  full_description: string | null
  website: string | null
  has_parking: boolean | null
  has_pool: boolean | null
  has_gym: boolean | null
  last_scraped_at: string | null
}

export interface ReportEntity {
  architect_id?: number
  developer_id?: number
  broker_id?: number
  name: string
}

export interface ReportEntities {
  architect: { architect_id: number; name: string } | null
  developer: { developer_id: number; name: string } | null
  broker: { broker_id: number; name: string } | null
}

export interface ReportTypologyRow {
  typology: string
  nr_units: number
  sold_units: number
  reserved_units: number
  avg_price_per_sqm: number | null
  min_area: number | null
  avg_area: number | null
  max_area: number | null
  min_price_unit: number | null
  avg_price_unit: number | null
  max_price_unit: number | null
  avg_price_change_percentage: number | null
}

export interface ReportSalesProgressionPoint {
  month: string
  cumulative_listed: number
  cumulative_reserved: number
  cumulative_sold: number
  cumulative_committed: number
}

// `statistics` payload mirrors `/projects/:id/statistics` which has many
// shape variants across the codebase — keep loose to avoid duplicating types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReportStatistics = any

export interface ReportTypologyBreakdown {
  data: ReportTypologyRow[]
  total: ReportTypologyRow | null
}

export interface ProjectReportData {
  project: ReportProject
  entities: ReportEntities
  statistics: ReportStatistics
  typology_breakdown: ReportTypologyBreakdown
  sales_progression: ReportSalesProgressionPoint[]
  static_map_url: string | null
  generated_at: string
}

/**
 * Fetches the aggregated payload that powers the Project Report PDF.
 * Disabled by default — pass `{ enabled: true }` from the modal so the
 * request only fires when the user actually opens it.
 */
export function useProjectReportData(
  projectId: number | null | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<ProjectReportData> {
  const { enabled = false } = options
  return useQuery<ProjectReportData>({
    queryKey: ['project-report-data', projectId],
    queryFn: () =>
      api.get(`/projects/${projectId}/report-data`).then((res) => res.data),
    enabled: enabled && !!projectId,
    staleTime: 5 * 60 * 1000,
  })
}
